<template>
  <ValidationProvider :name="name" :rules="rules" v-slot="{ failedRules }" slim>
    <div class="field">
      <vue-tel-input
        v-if="!disabled"
        role="textbox"
        :aria-label="name"
        @input="handleValue"
        @country-changed="handleCountryChanged"
        :enabledCountryCode="enabledCountryCode"
        :inputOptions="inputOptions"
        :maxLen="maxLen"
        :placeholder="placeholder"
        :defaultCountry="value && value.countryCode"
        :value="value && value.number ? value.number : null"
        :validCharactersOnly="true"
        autocomplete="off"
        :enabledFlags="!disableFlags"
        ref="telInput"
        mode="national"
        class="phone__input"
      />
      <input
        v-else
        role="textbox"
        :aria-label="name"
        type="text"
        :id="name"
        :name="name"
        autocomplete="off"
        class="field__input"
        :placeholder="disabledValue ? number : translate('ppNotSpecified')"
        :value="disabledValue ? disabledValue : translate('ppNotSpecified')"
        :style="{ color: disabledValue ? 'black' : '#cccccc' }"
        :disabled="true"
      />
      <div v-if="failedRules.required" class="field__error">
        {{ translate('rwThisIsRequired') }}
      </div>
      <label v-if="disabled" :for="name" class="field__label">{{ placeholder }}</label>
      <input
        v-model="countryCode"
        :name="`${name}.countryCode`"
        role="textbox"
        :aria-label="`${name}.countryCode`"
        type="text"
        class="mt-5 d-none"
      />
      <input
        v-model="number"
        :name="`${name}.number`"
        role="textbox"
        :aria-label="`${name}.number`"
        type="text"
        class="mt-5 d-none"
      />
    </div>
  </ValidationProvider>
</template>

<script>
import { VueTelInput } from 'vue-tel-input'
import { ValidationProvider } from 'vee-validate'

export default {
  components: {
    VueTelInput,
    ValidationProvider
  },
  props: {
    enabledCountryCode: {
      type: Boolean,
      default: false
    },
    inputOptions: {
      type: Object,
      default() {
        return { showDialCode: false }
      }
    },
    name: {
      type: String
    },
    placeholder: {
      type: String
    },
    defaultCountry: {
      type: String,
      default: ''
    },
    disableFlags: {
      type: Boolean,
      defaultValue: false
    },
    value: {
      type: Object,
      defaultValue: {
        countryCode: '',
        number: ''
      }
    },
    maxLen: {
      type: Number,
      default: 14
    },
    disabledValue: {
      type: String,
      defaultValue: ''
    },
    disabled: {
      type: Boolean,
      defaultValue: false
    },
    rules: {
      type: [Object, String],
      default: ''
    }
  },
  data: () => ({
    countryCode: '',
    number: '',
    innerValue: ''
  }),
  mounted() {
    this.countryCode = (this.value || {}).countryCode
    this.number = (this.value || {}).number

    this.$nextTick(() => {
      this.reorderDropdownItems()
    })
  },
  methods: {
    handleValue(_, { country, number }) {
      this.$emit('updatePhone', number.input)
      this.countryCode = (country || {}).iso2
      this.number = (number || {}).national
      if (
        number.e164 &&
        number.e164.length >= 8 &&
        this.countryCode.length > 0 &&
        this.number.length > 0
      ) {
        this.$emit('changed', {
          countryCode: this.countryCode,
          number: this.number
        })
      }
    },
    handleCountryChanged(country) {
      this.countryCode = country.iso2
      this.$nextTick(() => {
        this.reorderDropdownItems()
      })
    },

    reorderText(name) {
      const regex = /^(.*?)\s*\((.*?)\)$/
      const match = name.match(regex)
      if (!match) return name

      const text = match[1].trim()
      let native = match[2].trim()

      native = native.replace(/[\u202A-\u202E\u200E\u200F]/g, '')

      if (document.dir === 'rtl') {
        return `(${native}) ${text}`
      }
      return `${text} (${native})`
    },

    reorderDropdownItems() {
      const items = document.querySelectorAll('.vti__dropdown-item strong')
      items.forEach(el => {
        el.textContent = this.reorderText(el.textContent)
      })
    }
  },
  watch: {
    defaultCountry(newVal) {
      this.$refs.telInput.choose(newVal)
    },
    value(newValue) {
      if (newValue) {
        this.countryCode = newValue.countryCode
        this.number = newValue.national
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$color-grey: #c2c3c5;

.field {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem 0;
  width: 100%;
  row-gap: 0.1rem;

  &__input {
    position: relative;
    font-size: 1.4rem;
    padding: 0.75rem 1rem;
    border: solid 1px $color-grey;
    border-radius: 0.4rem;
    width: 100%;
    &[disabled] {
      border: 0;
      background-color: white;
    }
  }

  &__label {
    position: absolute;
    background: white;
    top: 1.85rem;
    left: 1rem;
    font-size: 1.4rem;
    transition: top 200ms ease-in, left 200ms ease-in, font-size 200ms ease-in;
  }

  &__error {
    color: crimson;
    font-size: 1.1rem;
  }
}

.field__input:focus ~ .field__label,
.field__input:not(:placeholder-shown).field__input:not(:focus) ~ .field__label {
  top: 0;
  font-size: 1.1rem;
}

.phone__input ::v-deep .vti__dropdown-list.below {
  text-align: start;
}
.phone__input ::v-deep .vti__input {
  padding-inline-start: 7px;
}
.phone__input ::v-deep .vti__selection {
  gap: 0.2rem;
}
.phone__input {
  padding: 0.18rem 0;
}

html[dir='rtl'] {
  .phone__input ::v-deep .vti__dropdown-list.below {
    right: 0;
  }
  .phone__input ::v-deep .vti__dropdown {
    border-left: 1px solid #d3d3d3;
  }
  .phone__input ::v-deep .vti__country-code,
  .phone__input ::v-deep .vti__dropdown-item span {
    direction: ltr;
    unicode-bidi: embed;
  }
  .phone__input ::v-deep .vti__dropdown-item {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
  .phone__input ::v-deep .vti__dropdown-item strong {
    direction: ltr;
    padding-inline-start: 4px;
  }
}
</style>
